import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarCheck,
  Info,
  Loader2,
  Lock,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/cn'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import { useControlAccounts } from '@/features/controlAccounts/queries'
import {
  useFiscalYears,
  usePreviewClose,
  useReverseClose,
  useRunClose,
} from '@/features/yearEndClose/queries'
import type {
  CloseStatus,
  FiscalYearSummary,
  YearEndClosePreview,
} from '@/features/yearEndClose/types'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return '-'
  return formatCurrencyPrecise(n)
}

function statusVariant(s: CloseStatus): 'success' | 'warning' | 'secondary' {
  if (s === 'completed') return 'success'
  if (s === 'reversed') return 'warning'
  return 'secondary'
}

function statusLabel(s: CloseStatus): string {
  if (s === 'completed') return 'Closed'
  if (s === 'reversed') return 'Reversed'
  return 'Open'
}

export function YearEndClosePage() {
  const { toast } = useToast()
  const regional = useRegionalDefaults()
  const [fiscalStartMonth, setFiscalStartMonth] = useState<number>(regional.fiscalStartMonth)
  // Pull tenant default once it's loaded; user-touched values aren't overwritten
  // because we only swap when still on the legacy 1-default. (Once they pick
  // a value other than 1, the effect leaves it alone.)
  useEffect(() => {
    if (!regional.isLoaded) return
    setFiscalStartMonth((cur) => (cur === 1 ? regional.fiscalStartMonth : cur))
  }, [regional.isLoaded, regional.fiscalStartMonth])
  const [previewTarget, setPreviewTarget] = useState<number | null>(null)
  const [previewData, setPreviewData] = useState<YearEndClosePreview | null>(null)
  const [previewErr, setPreviewErr] = useState<string | null>(null)
  const [reverseTarget, setReverseTarget] = useState<number | null>(null)
  // Typed-phrase confirm gate. Year-end close is hard-to-reverse (it
  // creates a real GL entry, locks every period, and only an admin
  // reversal undoes it). Force the user to actually type the year so a
  // mis-click can't end the books for the wrong period.
  const [confirmText, setConfirmText] = useState('')
  const expectedConfirm = previewTarget != null ? `CLOSE FY${previewTarget}` : ''
  const confirmMatches = confirmText.trim().toUpperCase() === expectedConfirm
  // Reverse close is also irreversible-ish - same gate.
  const [reverseConfirmText, setReverseConfirmText] = useState('')
  const expectedReverseConfirm = reverseTarget != null ? `REVERSE FY${reverseTarget}` : ''
  const reverseConfirmMatches = reverseConfirmText.trim().toUpperCase() === expectedReverseConfirm

  const q = useFiscalYears()
  const previewMut = usePreviewClose()
  const closeMut = useRunClose()
  const reverseMut = useReverseClose()
  const controlAccountsQ = useControlAccounts()
  // RE label is tenant-driven: pulls the configured Retained Earnings
  // account code from the Control Accounts setting (lives on the CoA
  // page). When a tenant renames it from 3900 to whatever, this label
  // updates automatically. Helper components below render it as a link
  // back to /coa so users can see + change the source.
  const reCode = controlAccountsQ.data?.control_accounts?.retained_earnings_code || ''
  const reLabel = reCode ? `Retained Earnings (${reCode})` : 'Retained Earnings'
  const reControlLink = (
    <Link to="/coa" className="font-semibold underline-offset-2 hover:underline">
      {reLabel}
    </Link>
  )

  const years = useMemo(() => q.data?.years ?? [], [q.data])

  const openPreview = async (year: number) => {
    setPreviewTarget(year)
    setPreviewData(null)
    setPreviewErr(null)
    setConfirmText('')
    try {
      const data = await previewMut.mutateAsync({ year, fiscalStartMonth })
      setPreviewData(data)
    } catch (err) {
      setPreviewErr(errorMessage(err))
    }
  }

  const runClose = async () => {
    if (previewTarget == null || !confirmMatches) return
    try {
      await closeMut.mutateAsync({ fiscalYear: previewTarget, fiscalStartMonth })
      toast({
        title: `FY${previewTarget} closed`,
        description:
          `Net income moved to ${reLabel} and all periods are now locked.`,
      })
      setPreviewTarget(null)
      setPreviewData(null)
      setConfirmText('')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Close failed',
        description: errorMessage(err),
      })
    }
  }

  const runReverse = async () => {
    if (reverseTarget == null || !reverseConfirmMatches) return
    try {
      await reverseMut.mutateAsync({ year: reverseTarget, fiscalStartMonth })
      toast({
        title: `FY${reverseTarget} reopened`,
        description:
          'All periods unlocked and the Retained Earnings closing entry was removed. Remember to re-close after corrections.',
      })
      setReverseTarget(null)
      setReverseConfirmText('')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Reversal failed',
        description: errorMessage(err),
      })
    }
  }

  return (
    <Page
      title="Year-End Close"
      description="Lock a fiscal year by transferring net income to retained earnings and sealing all accounting periods."
      width="wide"
    >
      <Alert>
        <Info className="size-4" />
        <AlertTitle>How year-end close works</AlertTitle>
        <AlertDescription>
          Fiscal years appear here automatically as soon as you post journal entries,
          invoices or bills with dates inside them - there is no &ldquo;open year&rdquo; step.
          When a year is ready, click <strong>Preview &amp; close</strong> to move its net
          income into {reControlLink}, zero every Income Statement account, and lock all of
          its periods. You can <strong>Reverse close</strong> later if corrections are
          needed; the close entry is removed and the periods reopen.
        </AlertDescription>
      </Alert>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium">Fiscal year start month</span>
                {/* The detail copy used to live below the select but
                    overwhelmed the page. Tucked behind a small ⓘ that
                    reveals on hover/focus so power users can still see
                    it without making first-time visitors wade through
                    a paragraph. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="What does fiscal year start month do?"
                      className="size-5 text-muted-foreground hover:bg-transparent hover:text-foreground"
                    >
                      <Info className="size-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs">
                    Sets which 12 months count as a fiscal year (e.g.{' '}
                    <span className="font-medium">April</span> → FY2025 = Apr&nbsp;2025–Mar&nbsp;2026;{' '}
                    <span className="font-medium">January</span> = calendar year). Defaults to your
                    saved value from Company → Regional Settings; changing it here only affects what
                    you preview and close on this page.
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                value={String(fiscalStartMonth)}
                onValueChange={(v) => setFiscalStartMonth(parseInt(v, 10))}
              >
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" onClick={() => void q.refetch()} disabled={q.isFetching}>
                {q.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
                Reload fiscal years
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="What does Reload fiscal years do?"
                    className="size-5 text-muted-foreground hover:bg-transparent hover:text-foreground"
                  >
                    <Info className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="max-w-xs">
                  Re-fetches the list of fiscal years from the server. Use it after posting new
                  journal entries, invoices or bills (a brand-new year may now exist) or after
                  finishing a close.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardContent>
      </Card>

      {q.isLoading ? (
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : q.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Could not load fiscal years: {errorMessage(q.error)}
          </AlertDescription>
        </Alert>
      ) : years.length === 0 ? (
        <Card>
          <CardContent className="py-6">
            <EmptyState
              icon={CalendarCheck}
              title="No fiscal years yet"
              description="Fiscal years aren't started manually - post your first journal entry, invoice or bill (any module that writes to the General Ledger) and the year will appear here ready to close."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {years.map((fy) => (
            <FiscalYearCard
              key={fy.fiscalYear}
              fy={fy}
              onPreview={() => openPreview(fy.fiscalYear)}
              onReverse={() => setReverseTarget(fy.fiscalYear)}
              previewBusy={previewMut.isPending && previewTarget === fy.fiscalYear}
              reverseBusy={reverseMut.isPending && reverseTarget === fy.fiscalYear}
            />
          ))}
        </div>
      )}

      <Modal
        open={previewTarget !== null}
        onOpenChange={(v) => {
          if (!v) {
            setPreviewTarget(null)
            setPreviewData(null)
            setPreviewErr(null)
            setConfirmText('')
          }
        }}
        title={previewTarget != null ? `Close Fiscal Year ${previewTarget}` : 'Close fiscal year'}
        description="Preview the closing entry before confirming."
        className="max-w-2xl"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setPreviewTarget(null)
                setPreviewData(null)
                setPreviewErr(null)
                setConfirmText('')
              }}
              disabled={closeMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={runClose}
              disabled={!previewData || closeMut.isPending || !confirmMatches}
            >
              {closeMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              Confirm close
            </Button>
          </>
        }
      >
        {previewMut.isPending ? (
          <div className="py-6">
            <LoadingSpinner />
          </div>
        ) : previewErr ? (
          <Alert variant="destructive">
            <AlertTitle>Cannot preview year-end close</AlertTitle>
            <AlertDescription>
              <p>{previewErr}</p>
              <p className="mt-2 text-sm">Ensure that:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                <li>All transactions are posted.</li>
                <li>The fiscal year has accounting data.</li>
              </ul>
            </AlertDescription>
          </Alert>
        ) : previewData ? (
          <div className="space-y-3">
            <Alert>
              <AlertTitle>About this close</AlertTitle>
              <AlertDescription>
                Closing FY{previewTarget} creates a Retained Earnings entry and locks all periods.
              </AlertDescription>
            </Alert>
            <div className="overflow-x-auto rounded-md border">
              <DataTable
                sortable={false}
                rows={Object.entries(previewData.breakdown).map(([category, amount]) => ({
                  category,
                  amount,
                }))}
                columns={[
                  { header: 'Category', accessorKey: 'category' },
                  {
                    header: () => <span className="block text-right">Amount</span>,
                    accessorKey: 'amount',
                    cell: ({ row }) => (
                      <span className="block text-right font-mono text-xs">
                        {fmtMoney(row.original.amount)}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border-t-2 bg-muted/50 px-3 py-2 text-sm font-semibold">
              <span>Net income</span>
              <span className="font-mono">{fmtMoney(previewData.netIncome)}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              This amount will be moved to {reControlLink}.
            </p>
            <Alert variant="default" className="border-warning bg-warning/10">
              <AlertTriangle className="size-4" />
              <AlertTitle>Before closing, verify</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                  <li>All transactions are posted to the General Ledger.</li>
                  <li>All bank accounts are reconciled.</li>
                  <li>All accruals and adjustments are recorded.</li>
                  <li>The Trial Balance is balanced.</li>
                </ul>
                <p className="mt-2 text-sm italic">
                  Once closed, periods will be locked and no entries can be posted until reversed.
                </p>
              </AlertDescription>
            </Alert>
            <div className="space-y-1.5 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <Label htmlFor="yec-confirm" className="text-sm font-semibold">
                Type <span className="font-mono">{expectedConfirm}</span> to confirm
              </Label>
              <Input
                id="yec-confirm"
                autoComplete="off"
                spellCheck={false}
                value={confirmText}
                placeholder={expectedConfirm}
                onChange={(e) => setConfirmText(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                This guard prevents an accidental close. The Confirm button enables once the phrase matches exactly.
              </p>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={reverseTarget !== null}
        onOpenChange={(v) => {
          if (!v) {
            setReverseTarget(null)
            setReverseConfirmText('')
          }
        }}
        title={reverseTarget != null ? `Reverse close - FY${reverseTarget}` : 'Reverse close'}
        className="max-w-lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setReverseTarget(null)
                setReverseConfirmText('')
              }}
              disabled={reverseMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={runReverse}
              disabled={reverseMut.isPending || !reverseConfirmMatches}
            >
              {reverseMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
              Reverse close
            </Button>
          </>
        }
      >
        {reverseTarget != null ? (
          <div className="space-y-3">
            <Alert variant="default" className="border-warning bg-warning/10">
              <AlertTriangle className="size-4" />
              <AlertTitle>Reversing FY{reverseTarget} will</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                  <li>Reopen all accounting periods for FY{reverseTarget}.</li>
                  <li>Remove the Retained Earnings closing entry.</li>
                  <li>Restore all Income Statement account balances.</li>
                </ul>
                <p className="mt-2 text-sm italic">
                  Only do this if corrections are needed. Audit trail is preserved.
                </p>
              </AlertDescription>
            </Alert>
            <div className="space-y-1.5 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <Label htmlFor="yec-reverse-confirm" className="text-sm font-semibold">
                Type <span className="font-mono">{expectedReverseConfirm}</span> to confirm
              </Label>
              <Input
                id="yec-reverse-confirm"
                autoComplete="off"
                spellCheck={false}
                value={reverseConfirmText}
                placeholder={expectedReverseConfirm}
                onChange={(e) => setReverseConfirmText(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>
        ) : null}
      </Modal>
    </Page>
  )
}

function FiscalYearCard({
  fy,
  onPreview,
  onReverse,
  previewBusy,
  reverseBusy,
}: {
  fy: FiscalYearSummary
  onPreview: () => void
  onReverse: () => void
  previewBusy: boolean
  reverseBusy: boolean
}) {
  const canClose = fy.closeStatus === 'open' || fy.closeStatus === 'reversed'
  const isClosed = fy.closeStatus === 'completed'
  const accent =
    isClosed ? 'border-success/40 bg-success/5'
    : fy.closeStatus === 'reversed' ? 'border-warning/40 bg-warning/5'
    : 'border-primary/40 bg-primary/5'
  return (
    <Card className={cn('overflow-hidden border-2', accent)}>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {isClosed ? (
              <Lock className="size-4 text-success" />
            ) : (
              <CalendarCheck className="size-4 text-primary" />
            )}
            <h3 className="text-lg font-semibold">FY {fy.fiscalYear}</h3>
          </div>
          <Badge variant={statusVariant(fy.closeStatus)}>{statusLabel(fy.closeStatus)}</Badge>
        </div>
        <dl className="space-y-1.5 text-sm">
          {fy.netIncome !== null ? (
            <Row label="Net income" value={fmtMoney(fy.netIncome)} mono />
          ) : null}
          {fy.closeDate ? (
            <Row label="Close date" value={new Date(fy.closeDate).toLocaleDateString()} />
          ) : null}
          {fy.closedAt ? (
            <Row label="Closed at" value={new Date(fy.closedAt).toLocaleString()} />
          ) : null}
        </dl>
        <Toolbar align="end">
          {canClose ? (
            <Button size="sm" onClick={onPreview} disabled={previewBusy}>
              {previewBusy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              Preview &amp; close
            </Button>
          ) : null}
          {isClosed ? (
            <Button size="sm" variant="danger" onClick={onReverse} disabled={reverseBusy}>
              <RotateCcw className="size-4" /> Reverse close
            </Button>
          ) : null}
        </Toolbar>
      </CardContent>
    </Card>
  )
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : ''}>{value}</dd>
    </div>
  )
}
